import { z } from 'zod';

/** Staged people imports (WP03): the same kinds, date systems and problem codes as the domain package. */
export const importKindNames = ['athletes', 'guardians', 'memberships'] as const;
export type ImportKindName = (typeof importKindNames)[number];

/** Largest CSV text accepted; the domain package enforces the same limit when parsing. */
/** Staged finance imports (WP06); finance only, under `/billing/imports`. */
export const financeImportKindNames = [
  'billing_accounts',
  'billing_assignments',
  'opening_balances',
  'payments',
] as const;
export type FinanceImportKindName = (typeof financeImportKindNames)[number];

export const importFileMaximumBytes = 512 * 1024;

export const importDateFormatNames = ['iso', 'dmy', 'mdy'] as const;
export type ImportDateFormatName = (typeof importDateFormatNames)[number];

export const importRowStatuses = [
  'ready',
  'error',
  'needs_decision',
  'already_imported',
  'skipped',
  'committed',
] as const;
export type ImportRowStatusName = (typeof importRowStatuses)[number];

export const importBatchStatuses = ['staged', 'committed', 'cancelled'] as const;

/**
 * Stages a UTF-8 CSV. The text travels in the JSON body; the file name is kept for traceability only.
 * The date system and the source name are required: nothing about either is guessed.
 */
export const stageImportRequestSchema = z.strictObject({
  kind: z.enum(importKindNames),
  /** Name of the system the file came from; with an external ID it identifies a record across imports. */
  source: z.string().trim().min(1).max(100),
  dateFormat: z.enum(importDateFormatNames),
  fileName: z.string().trim().min(1).max(255),
  content: z.string().min(1),
});
export type StageImportRequest = z.infer<typeof stageImportRequestSchema>;

export const stageFinanceImportRequestSchema = stageImportRequestSchema.extend({
  kind: z.enum(financeImportKindNames),
});
export type StageFinanceImportRequest = z.infer<typeof stageFinanceImportRequestSchema>;

/** What to do with a row that matches an existing athlete by name and birth date. */
export const decideImportRowRequestSchema = z
  .strictObject({
    decision: z.enum(['create', 'link', 'skip']),
    /** The existing athlete to map this row to; required for `link`, and one of the row's candidates. */
    athleteId: z.uuid().optional(),
  })
  .refine((request) => (request.decision === 'link') === (request.athleteId !== undefined), {
    path: ['athleteId'],
  });
export type DecideImportRowRequest = z.infer<typeof decideImportRowRequestSchema>;

/** What the rows of a finance batch add up to, per currency and kind of entry. */
export interface ImportTotalView {
  currency: string;
  type: 'opening_due' | 'opening_credit' | 'payment';
  count: number;
  /** Minor units as a decimal string. */
  amountMinor: string;
}

export interface ImportBatchView {
  id: string;
  kind: ImportKindName | FinanceImportKindName;
  source: string;
  dateFormat: ImportDateFormatName;
  fileName: string;
  status: (typeof importBatchStatuses)[number];
  rowCount: number;
  committedAt: string | null;
  /** Row version; send it in `If-Match` when cancelling. */
  version: number;
  createdAt: string;
  counts: Record<ImportRowStatusName, number>;
  /** Finance batches only: the rows that are ready or imported, to compare with the source sheet. */
  totals?: ImportTotalView[];
}

export interface ImportProblemView {
  column: string;
  /** One of the `import.problem.*` translation key suffixes. */
  code: string;
}

export interface ImportCandidateView {
  athleteId: string;
  givenName: string;
  familyName: string;
  dateOfBirth: string;
}

export interface ImportRowView {
  id: string;
  rowNumber: number;
  original: Record<string, string>;
  status: ImportRowStatusName;
  problems: ImportProblemView[];
  candidates: ImportCandidateView[];
  decision: 'create' | 'link' | 'skip' | null;
  decisionAthleteId: string | null;
  /** Row version; send it in `If-Match` when deciding. */
  version: number;
}

export interface ImportRowsPage {
  items: ImportRowView[];
  /** Pass as `after` for the next page; null on the last page. */
  nextAfter: number | null;
}

export interface ImportCommitView {
  batch: ImportBatchView;
  created: number;
  linked: number;
  skipped: number;
  alreadyImported: number;
  /** Finance batches only: what this commit posted. */
  totals?: ImportTotalView[];
}
